/**
 * This file contains the metadata types that are used to filter which auto triggers are executed
 * when a particular event occurs. This metadata is stored in the database and can be matched
 * against when determining the relevant auto triggers to execute.
 */

import { ActionAutoTriggerType } from '@doss/prisma/types'
import _ from 'lodash'
import * as yup from 'yup'
import { AmazonSellerSellingRegion } from './integration-metadata.js'

// Utilities

/**
 * When set, only fires for the specified integration instance.
 * Omitting the field, or a cleared form value (`null`), fires for all instances.
 * The admin select writes `null` when the field is cleared.
 */
const optionalWebhookIntegrationInstanceId = yup.string().uuid().nullable().optional()

/**
 * Shopify webhooks supported in Doss.
 *
 * Reference: https://shopify.dev/docs/api/admin-rest/2023-07/resources/webhook#event-topics
 *
 * NOTE: Remember to update shopify.app.production.toml and shopify.app.staging.toml when changing
 * this enum.
 */
export enum SupportedShopifyWebhookTopics {
  // Required topics
  APP_UNINSTALLED = 'app/uninstalled',
  CUSTOMERS_DATA_REQUEST = 'customers/data_request',
  CUSTOMERS_REDACT = 'customers/redact',
  SHOP_REDACT = 'shop/redact',

  ORDERS_CREATE = 'orders/create',
  ORDERS_UPDATED = 'orders/updated',
  ORDERS_DELETE = 'orders/delete',
  ORDERS_CANCELLED = 'orders/cancelled',
  ORDERS_PAID = 'orders/paid',
  INVENTORY_ITEMS_CREATE = 'inventory_items/create',
  INVENTORY_ITEMS_UPDATE = 'inventory_items/update',
  INVENTORY_ITEMS_DELETE = 'inventory_items/delete',
  INVENTORY_LEVELS_CONNECT = 'inventory_levels/connect',
  INVENTORY_LEVELS_UPDATE = 'inventory_levels/update',
  INVENTORY_LEVELS_DISCONNECT = 'inventory_levels/disconnect',
  LOCATIONS_CREATE = 'locations/create',
  LOCATIONS_UPDATE = 'locations/update',
  LOCATIONS_DELETE = 'locations/delete',
  COMPANIES_CREATE = 'companies/create',
  COMPANIES_UPDATE = 'companies/update',
  COMPANIES_DELETE = 'companies/delete',
  COMPANY_CONTACTS_CREATE = 'company_contacts/create',
  COMPANY_CONTACTS_UPDATE = 'company_contacts/update',
  COMPANY_CONTACTS_DELETE = 'company_contacts/delete',
  CUSTOMERS_CREATE = 'customers/create',
  CUSTOMERS_UPDATE = 'customers/update',
  CUSTOMERS_DELETE = 'customers/delete',
  PRODUCTS_CREATE = 'products/create',
  PRODUCTS_UPDATE = 'products/update',
  PRODUCTS_DELETE = 'products/delete',
  RETURNS_REQUEST = 'returns/request',
  RETURNS_UPDATE = 'returns/update',
  RETURNS_CLOSE = 'returns/close',
  RETURNS_APPROVE = 'returns/approve',
  RETURNS_DECLINE = 'returns/decline',
  RETURNS_CANCEL = 'returns/cancel',
  RETURNS_REOPEN = 'returns/reopen',
  REFUNDS_CREATE = 'refunds/create',
  DISPUTES_CREATE = 'disputes/create',
  DISPUTES_UPDATE = 'disputes/update',
  FULFILLMENTS_CREATE = 'fulfillments/create',
  FULFILLMENTS_UPDATE = 'fulfillments/update',
  FULFILLMENT_ORDERS_ORDER_ROUTING_COMPLETE = 'fulfillment_orders/order_routing_complete',
  FULFILLMENT_ORDERS_SPLIT = 'fulfillment_orders/split',
  FULFILLMENT_ORDERS_MOVED = 'fulfillment_orders/moved',
  FULFILLMENT_ORDERS_CANCELLED = 'fulfillment_orders/cancelled',
  FULFILLMENT_ORDERS_PLACED_ON_HOLD = 'fulfillment_orders/placed_on_hold',
  FULFILLMENT_ORDERS_SCHEDULED_FULFILLMENT_ORDER_READY = 'fulfillment_orders/scheduled_fulfillment_order_ready',
  FULFILLMENT_ORDERS_FULFILLMENT_REQUEST_REJECTED = 'fulfillment_orders/fulfillment_request_rejected',
  FULFILLMENT_ORDERS_FULFILLMENT_REQUEST_ACCEPTED = 'fulfillment_orders/fulfillment_request_accepted',
  FULFILLMENT_ORDERS_CANCELLATION_REQUEST_SUBMITTED = 'fulfillment_orders/cancellation_request_submitted',
  FULFILLMENT_ORDERS_CANCELLATION_REQUEST_ACCEPTED = 'fulfillment_orders/cancellation_request_accepted',
  FULFILLMENT_ORDERS_CANCELLATION_REQUEST_REJECTED = 'fulfillment_orders/cancellation_request_rejected',
  FULFILLMENT_EVENTS_CREATE = 'fulfillment_events/create',
}

/**
 * Checks whether the given topic is a supported Shopify webhook topic
 */
export function isSupportedShopifyWebhookTopic(
  topic: string,
): topic is SupportedShopifyWebhookTopics {
  return Object.values(SupportedShopifyWebhookTopics).includes(
    topic as SupportedShopifyWebhookTopics,
  )
}

/**
 * Quickbooks webhooks supported in Doss.
 *
 * https://developer.intuit.com/app/developer/qbo/docs/develop/webhooks
 */
export enum SupportedQuickbooksWebhookTopics {
  INVOICE_CREATE = 'INVOICE_CREATE',
  INVOICE_PAID = 'INVOICE_PAID',
  INVOICE_UPDATE = 'INVOICE_UPDATE',
  PAYMENT_CREATE = 'PAYMENT_CREATE',
  PAYMENT_UPDATE = 'PAYMENT_UPDATE',
  PURCHASEORDER_UPDATE = 'PURCHASEORDER_UPDATE',
  PURCHASEORDER_CREATE = 'PURCHASEORDER_CREATE',
  BILL_CREATE = 'BILL_CREATE',
  BILL_UPDATE = 'BILL_UPDATE',
  VENDOR_CREATE = 'VENDOR_CREATE',
  VENDOR_UPDATE = 'VENDOR_UPDATE',
  CUSTOMER_CREATE = 'CUSTOMER_CREATE',
  CUSTOMER_UPDATE = 'CUSTOMER_UPDATE',
  ACCOUNT_CREATE = 'ACCOUNT_CREATE',
  ACCOUNT_UPDATE = 'ACCOUNT_UPDATE',
}

/**
 * Checks if the incoming webhook entity is a supported Quickbooks webhook topic
 */
export function isSupportedQuickbooksWebhookTopic(
  topic: string,
): topic is SupportedQuickbooksWebhookTopics {
  return topic in SupportedQuickbooksWebhookTopics
}

/**
 * Gmail webhooks supported in Doss.
 *
 * Reference: https://developers.google.com/gmail/api/guides/push
 */
export enum SupportedGmailWebhookTopics {
  INTEGRATION_ENABLED = 'INTEGRATION_ENABLED',
  EMAIL_RECEIVED = 'EMAIL_RECEIVED',
  EMAIL_SENT = 'EMAIL_SENT',
}

/**
 * Outlook webhooks supported in Doss.
 *
 * Reference: https://learn.microsoft.com/en-us/graph/api/resources/subscription
 */
export enum SupportedOutlookWebhookTopics {
  NEW_MESSAGE = 'NEW_MESSAGE',
}

/**
 * Supported Logiwa webhook topics.
 *
 * Reference: https://www.logiwa.com/blog/logiwa-webhooks
 */
export enum SupportedLogiwaWebhookTopics {
  RECEIPT_INFO = 'RECEIPT_INFO',
  INVENTORY_CHANGED = 'INVENTORY_CHANGED',
}

/**
 * Supported Think Logistics webhook topics.
 * PO receipt → receipts GET; PO status → pofull GET.
 * Inbound shipment → ASN GET; outbound shipment → shipment GET.
 */
export enum SupportedThinkLogisticsWebhookTopics {
  PO_RECEIPT = 'PO_RECEIPT',
  PO_STATUS = 'PO_STATUS',
  INBOUND_SHIPMENT = 'INBOUND_SHIPMENT',
  OUTBOUND_SHIPMENT = 'OUTBOUND_SHIPMENT',
}

/**
 * Supported LOCAD seller webhook callback types (callback_type field on inbound payloads).
 * https://developer.golocad.com/#tag/Seller-Webhooks/operation/openapi_seller_callback_event_types_retrieve
 * - CustomOrderStatusUpdate: read-only order status and tracking sync for custom orders.
 * - ShipmentOrderStatusUpdate: shipment order status, carrier tracking, and waybill readiness.
 * - InboundStatusUpdate: inbound consignment status and received product details.
 * - ReturnOrderStatusUpdate: return order status. Event body matches Retrieve Return Order,
 *   plus callback_event_id and event_at.
 * - ReportDownloadLink: a generated report is ready to download. Product Statement Report
 *   arrives with report_type "Product Statement Report". The file rows are at download_url.
 * - CustomProductStockUpdate: catalogue product stock changed at a location. Refresh that SKU
 *   with the existing inventory snapshot read.
 */
export enum SupportedLocadWebhookTopics {
  CUSTOM_ORDER_STATUS_UPDATE = 'CustomOrderStatusUpdate',
  SHIPMENT_ORDER_STATUS_UPDATE = 'ShipmentOrderStatusUpdate',
  INBOUND_STATUS_UPDATE = 'InboundStatusUpdate',
  RETURN_ORDER_STATUS_UPDATE = 'ReturnOrderStatusUpdate',
  REPORT_DOWNLOAD_LINK = 'ReportDownloadLink',
  CUSTOM_PRODUCT_STOCK_UPDATE = 'CustomProductStockUpdate',
}

/**
 * Supported WooCommerce webhook topics.
 * See all supported topics: https://woocommerce.github.io/woocommerce-rest-api-docs/#topics
 */
export enum SupportedWooCommerceWebhookTopics {
  ORDER_CREATED = 'ORDER_CREATED',
  ORDER_UPDATED = 'ORDER_UPDATED',
  ORDER_DELETED = 'ORDER_DELETED',
}

/**
 * Supported Loop Returns webhook topics.
 * See all supported topics: https://docs.loopreturns.com/webhook-reference/topics/return-webhook
 */
export enum SupportedLoopReturnsWebhookTopics {
  PROCESS_RETURN = 'PROCESS_RETURN',
  RESTOCK = 'RESTOCK',
  LABEL = 'LABEL',
  LABEL_REQUEST = 'LABEL_REQUEST',
  HAPPY_RETURNS_SHIPMENT = 'HAPPY_RETURNS_SHIPMENT',
  GIFT_CARD = 'GIFT_CARD',
}

/**
 * Supported Extensiv webhook topics.
 *
 * Reference: https://help.extensiv.com/en_US/rest-api/configuring-webhooks
 */
export enum SupportedExtensivWebhookTopics {
  ORDER_UPDATED = 'ORDER_UPDATED',
}

/**
 * Supported Flexport webhook topics.
 *
 * Reference: https://apidocs.flexport.com/v2/tag/Milestones/
 */
export enum SupportedFlexportWebhookTopics {
  SHIPMENT = 'SHIPMENT',
  SHIPMENT_LEG = 'SHIPMENT_LEG',
  CONTAINER_LEG = 'CONTAINER_LEG',
}

/**
 * Supported Stripe webhook topics.
 *
 * Reference: https://docs.stripe.com/api/events/list
 */
export enum SupportedStripeWebhookTopics {
  PAYMENT_SUBMITTED = 'PAYMENT_SUBMITTED',
  PRODUCT_CREATED = 'PRODUCT_CREATED',
  PRICE_CREATED = 'PRICE_CREATED',
  PRICE_UPDATED = 'PRICE_UPDATED',
  PRODUCT_UPDATED = 'PRODUCT_UPDATED',
}

/**
 * Supported IngramMicro webhook topics
 *
 * Reference: https://developer.ingramMicromicro.com/reseller/api-documentation/order-status-notification-events
 */
export enum SupportedBillWebhookTopics {
  BILL_CREATED = 'bill.created',
  BILL_UPDATED = 'bill.updated',
  PAYMENT_UPDATED = 'payment.updated',
}

export enum SupportedIngramMicroWebhookTopics {
  ORDER_UPDATED = 'ORDER_UPDATED',
}

/**
 * Jay Group webhooks supported in Doss — Phase 1 event set per PHP-178.
 *
 * Reference: https://stage.api.logistics.jaygroup.com/index.html?url=/docs/v3/open-api.json
 */
/**
 * GoFreight webhooks supported in Doss.
 *
 * Reference: https://developers.gofreight.com/docs/webhooks/webhook_events/
 */
export enum SupportedGofreightWebhookTopics {
  OI_HBL_CREATED = 'oi_hbl.created',
  OI_HBL_UPDATED = 'oi_hbl.updated',
  OI_MBL_CREATED = 'oi_mbl.created',
  OI_MBL_UPDATED = 'oi_mbl.updated',
  CONTAINER_CREATED = 'container.created',
  CONTAINER_UPDATED = 'container.updated',
}

export enum SupportedJayGroupWebhookTopics {
  ORDER_CREATED = 'OrderCreated',
  ORDER_UPDATED = 'OrderUpdated',
  ORDER_CANCELED = 'OrderCanceled',
  ORDER_HELD = 'OrderHeld',
  RECEIVING_COMPLETED = 'ReceivingCompleted',
  RECEIVING_HELD = 'ReceivingHeld',
  RECEIVING_CANCELED = 'ReceivingCanceled',
  FULFILLMENT_SHIPMENT_CONFIRMED = 'FulfillmentShipmentConfirmed',
  FULFILLMENT_INTRANSIT = 'FulfillmentInTransit',
  FULFILLMENT_DELIVERED = 'FulfillmentDelivered',
  FULFILLMENT_SHIPMENT_EXCEPTION = 'FulfillmentShipmentException',
  FULFILLMENT_RETURNED_TO_SENDER = 'FulfillmentReturnedToSender',
  PURCHASE_ORDER_CONFIRMED = 'PurchaseOrderConfirmed',
  PURCHASE_ORDER_FULFILLED = 'PurchaseOrderFulfilled',
  PURCHASE_ORDER_CANCELED = 'PurchaseOrderCanceled',
}

/**
 * Supported Amazon Seller webhook topics
 */
export enum SupportedAmazonSellerWebhookTopics {
  ORDER_CHANGE = 'ORDER_CHANGE',
  REPORT_PROCESSING_FINISHED = 'REPORT_PROCESSING_FINISHED',
  FBA_INVENTORY_AVAILABILITY_CHANGES = 'FBA_INVENTORY_AVAILABILITY_CHANGES',
  FBA_OUTBOUND_SHIPMENT_STATUS = 'FBA_OUTBOUND_SHIPMENT_STATUS',
  FULFILLMENT_ORDER_STATUS = 'FULFILLMENT_ORDER_STATUS',
}

/**
 * Checks whether the given topic is a supported Amazon Seller webhook topic
 */
export function isSupportedAmazonSellerWebhookTopic(
  topic: unknown,
): topic is SupportedAmazonSellerWebhookTopics {
  return Object.values(SupportedAmazonSellerWebhookTopics).includes(
    topic as SupportedAmazonSellerWebhookTopics,
  )
}

/**
 * Doss Banking webhooks supported in Doss.
 *
 * Reference: https://docs.stripe.com/api/events/types?api-version=2025-05-28.basil
 */
export enum SupportedDossBankingWebhookTopics {
  REFRESHED_BALANCE = 'REFRESHED_BALANCE',
  REFRESHED_TRANSACTIONS = 'REFRESHED_TRANSACTIONS',
}

/**
 * Doss Bill Pay webhooks supported in Doss.
 *
 * https://developer.bill.com/docs/payment-webhook-notification-payloads
 */
export enum SupportedDossBillPayWebhookTopics {
  PAYMENT_UPDATED = 'PAYMENT_UPDATED',
  PAYMENT_FAILED = 'PAYMENT_FAILED',
}

/**
 * HubSpot webhooks supported in Doss.
 *
 * https://app.hubspot.com/developer/47961841/application/4371801/webhooks
 */
export enum SupportedHubSpotWebhookTopics {
  DEAL_STAGE_UPDATE = 'DEAL_STAGE_UPDATE',
}

/**
 * Salsify webhooks supported in Doss.
 *
 * https://developers.salsify.com/docs/imports-webhooks
 */
export enum SupportedSalsifyWebhookTopics {
  PRODUCT_CHANGE = 'PRODUCT_CHANGE',
  DIGITAL_ASSET_CHANGE = 'DIGITAL_ASSET_CHANGE',
}

/**
 * ShipBob webhooks supported in Doss.
 *
 * https://developer.shipbob.com/webhooks/
 */
export enum SupportedShipBobWebhookTopics {
  ORDER_SHIPPED = 'ORDER_SHIPPED',
  ORDER_SHIPMENT_CANCELLED = 'ORDER_SHIPMENT_CANCELLED',
  ORDER_SHIPMENT_DELIVERED = 'ORDER_SHIPMENT_DELIVERED',
  ORDER_DELIVERED = 'ORDER_DELIVERED',
  ORDER_SHIPMENT_EXCEPTION = 'ORDER_SHIPMENT_EXCEPTION',
  ORDER_SHIPMENT_ON_HOLD = 'ORDER_SHIPMENT_ON_HOLD',
  ORDER_SHIPMENT_TRACKING_UPDATED = 'ORDER_SHIPMENT_TRACKING_UPDATED',
  WRO_CREATED = 'WRO_CREATED',
  WRO_UPDATED = 'WRO_UPDATED',
  WRO_COMPLETED = 'WRO_COMPLETED',
  WRO_BOX_ARRIVED = 'WRO_BOX_ARRIVED',
  WRO_BOX_SCANNED = 'WRO_BOX_SCANNED',
  WRO_BOX_STOWED = 'WRO_BOX_STOWED',
}

/**
 * ShipHero webhooks supported in Doss.
 *
 * https://developer.shiphero.com/webhooks#inventory-change-webhook
 */
export enum SupportedShipHeroWebhookTopics {
  INVENTORY_CHANGE = 'Inventory Change',
  PO_UPDATE = 'PO Update',
  SHIPMENT_UPDATE = 'Shipment Update',
  INVENTORY_UPDATE = 'Inventory Update',
  /** POST body delivered to post_url when inventory_generate_snapshot completes */
  INVENTORY_SNAPSHOT_POST = 'Inventory Snapshot Post',
  /** Fires when a return is created or updated (includes quantity_received per line item) */
  RETURN_UPDATE = 'Return Update',
  /** Fires when an order is canceled in ShipHero (e.g. warehouse-side rejection) */
  ORDER_CANCELED = 'Order Canceled',
  /** Fires when an order is allocated in ShipHero */
  ORDER_ALLOCATED = 'Order Allocated',
  /** Fires when an order is deallocated in ShipHero */
  ORDER_DEALLOCATED = 'Order Deallocated',
  /** Fires when an order is packed out from a tote in ShipHero */
  ORDER_PACKED_OUT = 'Order Packed Out',
  /** Fires when a work order changes status (e.g. assembly kitting progress) */
  WORK_ORDER_STATUS_UPDATE = 'Work Order Status Update',
}

/**
 * CartonCloud webhooks supported in Doss.
 *
 * CartonCloud pushes one self-describing envelope for Purchase Orders (INBOUND / stock increase),
 * Sale Orders (OUTBOUND / stock decrement), and Stock Changes (`details.isAdjustment === true`). We
 * classify each payload onto these two topics using the `type` + `details.isAdjustment` fields.
 * https://api-docs.cartoncloud.com/#webhooks
 */
export enum SupportedCartonCloudWebhookTopics {
  /** Inbound shipment receipt — a Purchase Order confirmation (type INBOUND, not an adjustment). */
  RECEIVING = 'Receiving',
  /** Any other inventory-level movement: a stock adjustment, or a Sale Order (OUTBOUND) decrement. */
  STOCK_UPDATE = 'Stock Update',
}

/**
 * ShipMonk webhooks supported in Doss.
 *
 * https://apidocs.shipmonk.com/reference/webhook-order-status-change
 */
export enum SupportedShipMonkWebhookTopics {
  RECEIVING_STATUS_CHANGE = 'receivingStatusChange',
  ORDER_SHIPMENT_NOTIFICATION = 'orderShipmentNotification',
  RETURN_STATUS_CHANGE = 'returnStatusChange',
}

/**
 * ShipWire webhooks supported in Doss.
 *
 * https://www.shipwire.com/docs/webhooks/
 */
export enum SupportedShipWireWebhookTopics {
  STOCK_ALERT = 'STOCK_ALERT',
  RECEIVING_CREATED = 'RECEIVING_CREATED',
}

/**
 * Gnosis webhooks supported in Doss.
 *
 * https://docs.gnosisfreight.com/reference/get_container_webhook_schema_api_v1_webhooks_containers_webhook_schema_get
 */
export enum SupportedGnosisWebhookTopics {
  CONTAINER = 'CONTAINER',
}

/**
 * Mintsoft webhooks supported in Doss.
 *
 * https://accessgroup.my.site.com/Support/s/article/Mintsoft-API-Order-Webhooks-How-can-I-get-notified-when-an-Order-has-been-despatched-or-cancelled-or-Delivered-or-Split?language=en_US
 */
export enum SupportedMintsoftWebhookTopics {
  ORDER_DISPATCHED = 'order_dispatched',
  ORDER_CANCELLED = 'order_cancelled',
  ORDER_DELIVERED = 'order_delivered',
  ORDER_SPLIT = 'order_split',
}

/**
 * Firstbase inventory order webhook events supported in Doss.
 * Reference: https://developer.firstbase.io/docs/webhooks
 */
export enum SupportedFirstbaseWebhookTopics {
  INVENTORY_ORDER_CREATED = 'inventory_order_created',
  INVENTORY_ORDER_UPDATED = 'inventory_order_updated',
}

/**
 * Feno webhook events supported in Doss.
 */
export enum SupportedFenoWebhookTopics {
  CUSTOMER_REGISTERED = 'customer.registered',
  CUSTOMER_ACTIVATED = 'customer.activated',
  CUSTOMER_UPDATED = 'customer.updated',
  CUSTOMER_MAGIC_LINK_REQUESTED = 'customer.magic_link_requested',
  CUSTOMER_PHONE_VERIFIED = 'customer.phone_verified',
  ORDER_PAID = 'order.paid',
  ORDER_DELIVERED = 'order.delivered',
  ORDER_REFUNDED = 'order.refunded',
  ORDER_PENDING_SIZING = 'order.pending_sizing',
  FITKIT_SHIPPED = 'fitkit.shipped',
  FITKIT_DELIVERED = 'fitkit.delivered',
  SIZING_COMPLETED = 'sizing.completed',
  ORDER_MOUTHPIECE_ASSIGNED = 'order.mouthpiece_assigned',
  MOUTHPIECE_REPLACEMENT_SHIPPED = 'mouthpiece.replacement_shipped',
  SUBSCRIPTION_UPDATED = 'subscription.updated',
  SUBSCRIPTION_PAST_DUE = 'subscription.past_due',
  SUBSCRIPTION_TRIAL_REDEEMED = 'subscription.trial_redeemed',
  CHECKOUT_CREATED = 'checkout.created',
}

export enum SupportedNavisphereWebhookTopics {
  ORDER_CREATED = 'order_created',
  ORDER_UPDATED = 'order_updated',
  ORDER_CANCELED = 'order_canceled',
  ORDER_REJECTED = 'order_rejected',
  ORDER_COMPLETED = 'order_completed',
  LOAD_CREATED = 'load_created',
  LOAD_BOOKED = 'load_booked',
  LOAD_CANCELLED = 'load_cancelled',
  CARRIER_ARRIVED = 'carrier_arrived',
  CARRIER_DEPARTED = 'carrier_departed',
  APPOINTMENT_UPDATED = 'appointment_updated',
  IN_TRANSIT = 'in_transit',
  CARGO_DELIVERED = 'cargo_delivered',
  PRO_NUMBER_ADDED = 'pro_number_added',
}

/**
 * a2b Fulfillment webhooks supported in Doss.
 *
 * https://developers.cartrover.com/#0c5837a3-56d2-4357-bca1-4166fd366c94
 */
export enum SupportedA2bFulfillmentWebhookTopics {
  ORDER_UPDATE = 'ORDER_UPDATE',
}

/**
 * TikTok Shop webhooks supported in Doss.
 *
 * https://partner.tiktokshop.com/docv2/page/650300b8a57708028b430b4a
 */
export enum SupportedTikTokShopWebhookTopics {
  ORDER_STATUS_CHANGE = 'ORDER_STATUS_CHANGE', // 1
  AUTH_EXPIRE = 'AUTH_EXPIRE', // 7
  FBT_ORDER_RETURN_STATUS_CHANGE = 'FBT_ORDER_RETURN_STATUS_CHANGE', // 12
  FBT_ORDER_STATUS_UPDATE = 'FBT_ORDER_STATUS_UPDATE', // 21
  FBT_MERCHANT_ONBOARDING = 'FBT_MERCHANT_ONBOARDING', // 22
  FBT_GOODS_MATCH = 'FBT_GOODS_MATCH', // 23
  FBT_INVENTORY_UPDATE = 'FBT_INVENTORY_UPDATE', // 24
  INVENTORY_STATUS_CHANGE = 'INVENTORY_STATUS_CHANGE', // 27
  INVOICE_STATUS_CHANGE = 'INVOICE_STATUS_CHANGE', // 36
}

/**
 * Maya webhooks supported in Doss.
 *
 * https://developer.activeants.com/specs/v3#tag/v3events/operation/event-shipment-shipped-3
 */
export enum SupportedMayaWebhookTopics {
  SHIPMENT_SHIPPED = 'SHIPMENT_SHIPPED',
}

/**
 * Misfits Market webhooks supported in Doss.
 *
 * Reference: https://fbm.misfitsmarket.com/
 */
export enum SupportedMisfitsMarketWebhookTopics {
  INVENTORY_UPDATE = 'INVENTORY_UPDATE',
  ITEM_CASE_ADJUSTMENT = 'ITEM_CASE_ADJUSTMENT',
  PACKAGING_CASE_ADJUSTMENT = 'PACKAGING_CASE_ADJUSTMENT',
}

/**
 * Airtable webhooks supported
 *
 * Reference: https://airtable.com/developers/web/api/model/webhooks-specification
 */
export enum SupportedAirtableWebhookTopics {
  ROW_CREATE = 'ROW_CREATE',
  ROW_UPDATE = 'ROW_UPDATE',
}

/**
 * Silq webhooks supported in Doss.
 *
 * Reference: https://silq.api-demo.onesilq.com/api/docs/#tag/Webhooks
 */
export enum SupportedSilqWebhookTopics {
  SHIPMENT_UPDATED = 'SHIPMENT_UPDATED',
}

/**
 * Stord webhooks supported in Doss.
 *
 * Canceled events (`*.canceled`) are separate topics from created (`*.created`) so workflows can
 * subscribe to one without the other. Payload shape is the same (`is_canceled`, `cancellation_id`).
 *
 * Reference: https://developers.stord.com/docs/webhooks
 */
export enum SupportedStordWebhookTopics {
  SHIPMENT_CONFIRMATION = 'SHIPMENT_CONFIRMATION',
  SHIPMENT_CONFIRMATION_CANCELED = 'SHIPMENT_CONFIRMATION_CANCELED',
  RECEIPT_CONFIRMATION = 'RECEIPT_CONFIRMATION',
  RECEIPT_CONFIRMATION_CANCELED = 'RECEIPT_CONFIRMATION_CANCELED',
  ORDER_UPDATE = 'ORDER_UPDATE',
}

/**
 * Routable webhooks supported in Doss.
 *
 * Reference: https://developers.routable.com/docs/webhooks
 */
export enum SupportedRoutableWebhookTopics {
  PAYABLE_STATUS_CHANGED = 'PAYABLE_STATUS_CHANGED',
}

/**
 * 40Seas webhooks supported in Doss.
 *
 * Reference: https://docs.40seas.com/webhooks/
 */
export enum SupportedFortySeasWebhookTopics {
  CREDITLINE_UPDATED = 'creditline.updated',
  CREDITLINE_UTILIZED = 'creditline.utilized',
  CREDITLINE_RELEASED = 'creditline.released',
  CREDITLINE_PREAPPROVED = 'creditline.preapproved',
  CREDITLINE_PENDING = 'creditline.pending',
  CREDITLINE_DECLINED = 'creditline.declined',
  CREDITLINE_APPROVED = 'creditline.approved',
  CREDITLINE_REQUEST_PENDING = 'creditline-request.pending',
  CREDITLINE_REQUEST_IN_REVIEW = 'creditline-request.in-review',
  CREDITLINE_REQUEST_BLOCKED = 'creditline-request.blocked',
  CREDITLINE_REQUEST_DECLINED = 'creditline-request.declined',
  CREDITLINE_REQUEST_CANCELLED = 'creditline-request.cancelled',
  CREDITLINE_REQUEST_APPROVED = 'creditline-request.approved',
  BUYER_CREATED = 'buyer.created',
  BUYER_UPDATED = 'buyer.updated',
  INVOICE_CREATED = 'invoice.created',
  INVOICE_UPDATED = 'invoice.updated',
  INVOICE_ELIGIBLE = 'invoice.eligible',
  INVOICE_CONFIRMED = 'invoice.confirmed',
  INVOICE_ASSIGNED = 'invoice.assigned',
  SHIPPING_PENDING = 'shipping.pending',
  SHIPPING_VALID = 'shipping.valid',
  SHIPPING_INVALID = 'shipping.invalid',
}

/**
 * USA Supply Portal webhooks supported in Doss.
 *
 * Reference: https://docs.google.com/document/d/17e6Pf_4cCn6iaht22q1XbEEbtBc9mY5d_nzRKva5ry4/
 */
export enum SupportedUSASupplyPortalWebhookTopics {
  COMMITMENT_CREATE = 'COMMITMENT_CREATE',
  COMMITMENT_DELETE = 'COMMITMENT_DELETE',
  COMMITMENT_UPDATE = 'COMMITMENT_UPDATE',
  NEW_BUYER_CREATE = 'NEW_BUYER_CREATE',
  NEW_BUYER_UPDATE = 'NEW_BUYER_UPDATE',
  PAYMENT_CREATE = 'PAYMENT_CREATE',
  PAYMENT_UPDATE = 'PAYMENT_UPDATE',
  TRACKING_NUMBER_CREATE = 'TRACKING_NUMBER_CREATE',
  TRACKING_NUMBER_DELETE = 'TRACKING_NUMBER_DELETE',
  TRACKING_NUMBER_UPDATE = 'TRACKING_NUMBER_UPDATE',
  ACH_ACCOUNT_CREATE = 'ACH_ACCOUNT_CREATE',
  ACH_ACCOUNT_UPDATE = 'ACH_ACCOUNT_UPDATE',
  CHECK_PAYEE_NAME_CREATE = 'CHECK_PAYEE_NAME_CREATE',
}

/**
 * EasyPost webhooks supported in Doss.
 *
 * Reference: https://docs.easypost.com/guides/webhooks-guide
 */
export enum SupportedEasyPostWebhookTopics {
  TRACKER_UPDATED = 'TRACKER_UPDATED',
  TRACKER_CREATED = 'TRACKER_CREATED',
}

export enum SupportedWalmartWebhookTopics {
  ORDER_UPDATE = 'ORDER_UPDATE',
}

/**
 * Rillet webhooks supported in Doss.
 *
 * Reference: https://docs.api.rillet.com/docs/webhooks
 */
export enum SupportedRilletWebhookTopics {
  BILL_CREATED = 'BILL_CREATED',
  BILL_UPDATED = 'BILL_UPDATED',
  BILL_DELETED = 'BILL_DELETED',
  CONTRACT_CREATED = 'CONTRACT_CREATED',
  CONTRACT_UPDATED = 'CONTRACT_UPDATED',
  CONTRACT_DELETED = 'CONTRACT_DELETED',
  CREDIT_MEMO_CREATED = 'CREDIT_MEMO_CREATED',
  CREDIT_MEMO_UPDATED = 'CREDIT_MEMO_UPDATED',
  CREDIT_MEMO_PAYMENT_UPDATED = 'CREDIT_MEMO_PAYMENT_UPDATED',
  CREDIT_MEMO_DELETED = 'CREDIT_MEMO_DELETED',
  CUSTOMER_CREATED = 'CUSTOMER_CREATED',
  CUSTOMER_UPDATED = 'CUSTOMER_UPDATED',
  CUSTOMER_DELETED = 'CUSTOMER_DELETED',
  INVOICE_CREATED = 'INVOICE_CREATED',
  INVOICE_UPDATED = 'INVOICE_UPDATED',
  INVOICE_PAYMENT_UPDATED = 'INVOICE_PAYMENT_UPDATED',
  INVOICE_DELETED = 'INVOICE_DELETED',
  JOURNAL_ENTRY_CREATED = 'JOURNAL_ENTRY_CREATED',
  JOURNAL_ENTRY_UPDATED = 'JOURNAL_ENTRY_UPDATED',
  JOURNAL_ENTRY_DELETED = 'JOURNAL_ENTRY_DELETED',
  VENDOR_CREATED = 'VENDOR_CREATED',
  VENDOR_UPDATED = 'VENDOR_UPDATED',
  VENDOR_DELETED = 'VENDOR_DELETED',
}

/**
 * Jazz webhooks supported in Doss
 *
 * https://www.jazz-oms.com/#webhooks
 */
export enum SupportedJazzWebhookTopics {
  INVENTORY_TRANSACTION_UPDATED = 'INVENTORY_TRANSACTION_UPDATED',
}

/**
 * Shopee webhooks supported in Doss.
 *
 * - order_status_push (code 3) notifies order status changes, including cancellation.
 *   The push has no actor, reason, or quantities. Recover those from get_order_detail.
 * - return_updates_push (code 29): return/refund field changes. The payload is a delta
 *   (`order_sn`, `return_sn`, old/new values). Authoritative return details are read from
 *   `returns.get_return_detail` using `return_sn`.
 *   https://open.shopee.com/push-mechanism/32
 * - package_fulfillment_status_push (code 30) notifies one package's fulfillment status.
 *   That state stays independent of order cancellation. Recover the package from get_package_detail.
 * https://open.shopee.com/developer-guide/20
 */
export enum SupportedShopeeWebhookTopics {
  ORDER_STATUS_PUSH = 'order_status_push',
  RETURN_UPDATES_PUSH = 'return_updates_push',
  PACKAGE_FULFILLMENT_STATUS_PUSH = 'package_fulfillment_status_push',
}

/**
 * Lazada Push Mechanism message types we listen to, for read-only order sync:
 *
 * - Trade Order (message_type 0): fires on order create/update/status change.
 *   https://open.lazada.com/apps/doc/doc?nodeId=29537&docId=120196
 * - Reverse Order (message_type 10): fires on some return/refund status changes.
 *   Lazada does not push every reverse state. Recover the current line from /order/items/get.
 *   https://open.lazada.com/apps/doc/doc?nodeId=29537&docId=120232
 * - Fulfillment Order (message_type 14): fires on fulfillment package status change,
 *   including loss, damage, delivery failure, and return-to-shipper.
 *   https://open.lazada.com/apps/doc/doc?nodeId=29537&docId=120211
 */
export enum SupportedLazadaWebhookTopics {
  FULFILLMENT_ORDER = 'fulfillment_order',
  REVERSE_ORDER = 'reverse_order',
  TRADE_ORDER = 'trade_order',
}

// Schemas
export const formSubmitAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.FORM_SUBMIT] as const)
      .required(),
    formId: yup.string().required(),
  })
  .strict()

export const formV2SubmitAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.FORM_V2_SUBMIT] as const)
      .required(),
    formId: yup.string().required(),
  })
  .strict()

export const addRowAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.ADD_ROW] as const)
      .required(),
    tableId: yup.string().required(),
  })
  .strict()

export const updateCellAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.UPDATE_CELL] as const)
      .required(),
    tableId: yup.string().required(),
    columnId: yup.string().required(),
  })
  .strict()

export const updateRowAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.UPDATE_ROW] as const)
      .required(),
    tableId: yup.string().required(),
  })
  .strict()

export const deleteRowAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.DELETE_ROW] as const)
      .required(),
    tableId: yup.string().required(),
  })
  .strict()

export const shopifyWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.SHOPIFY_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedShopifyWebhookTopics)).required(),
    integrationId: optionalWebhookIntegrationInstanceId,
  })
  .strict()

export const demoAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.DEMO] as const)
      .required(),
    description: yup.string(),
  })
  // Allow unknown fields for demo triggers
  .strict(false)
  .noUnknown(false)

export const quickbooksWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.QUICKBOOKS_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedQuickbooksWebhookTopics)).required(),
    integrationId: optionalWebhookIntegrationInstanceId,
  })
  .strict()

export const gmailWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.GMAIL_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedGmailWebhookTopics)).required(),
    integrationId: optionalWebhookIntegrationInstanceId,
  })
  .strict()

export const gnosisWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.GNOSIS_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedGnosisWebhookTopics)).required(),
  })
  .strict()

export const outlookWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.OUTLOOK_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedOutlookWebhookTopics)).required(),
    integrationId: optionalWebhookIntegrationInstanceId,
  })
  .strict()

export const wooCommerceWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.WOO_COMMERCE_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedWooCommerceWebhookTopics)).required(),
  })
  .strict()
export const loopReturnsWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.LOOP_RETURNS_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedLoopReturnsWebhookTopics)).required(),
  })
  .strict()

export const logiwaWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.LOGIWA_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedLogiwaWebhookTopics)).required(),
  })
  .strict()

export const thinkLogisticsWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.THINK_LOGISTICS_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedThinkLogisticsWebhookTopics)).required(),
  })
  .strict()

export const locadWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.LOCAD_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedLocadWebhookTopics)).required(),
    integrationId: optionalWebhookIntegrationInstanceId,
  })
  .strict()

export const flexportWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.FLEXPORT_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedFlexportWebhookTopics)).required(),
  })
  .strict()

export const extensivWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.EXTENSIV_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedExtensivWebhookTopics)).required(),
    integrationId: optionalWebhookIntegrationInstanceId,
  })
  .strict()

export const stripeWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.STRIPE_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedStripeWebhookTopics)).required(),
    integrationId: optionalWebhookIntegrationInstanceId,
  })
  .strict()

export const billWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.BILL_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedBillWebhookTopics)).required(),
  })
  .strict()

export const ingramMicroWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.INGRAM_MICRO_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedIngramMicroWebhookTopics)).required(),
  })
  .strict()

export const jayGroupWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.JAY_GROUP_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedJayGroupWebhookTopics)).required(),
  })
  .strict()

export const gofreightWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.GOFREIGHT_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedGofreightWebhookTopics)).required(),
    integrationId: optionalWebhookIntegrationInstanceId,
  })
  .strict()

export const amazonSellerWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.AMAZON_SELLER_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedAmazonSellerWebhookTopics)).required(),
    endpointRegion: yup
      .array()
      .of(yup.string().oneOf(Object.values(AmazonSellerSellingRegion)).required())
      .optional()
      .nullable(),
    integrationId: optionalWebhookIntegrationInstanceId,
  })
  .strict()

export const hubSpotWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.HUBSPOT_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedHubSpotWebhookTopics)).required(),
  })
  .strict()

export const shipBobWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.SHIPBOB_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedShipBobWebhookTopics)).required(),
  })
  .strict()

export const shipHeroWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.SHIPHERO_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedShipHeroWebhookTopics)).required(),
    integrationId: optionalWebhookIntegrationInstanceId,
  })
  .strict()

export const shipMonkWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.SHIPMONK_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedShipMonkWebhookTopics)).required(),
  })
  .strict()

export const cartonCloudWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.CARTONCLOUD_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedCartonCloudWebhookTopics)).required(),
  })
  .strict()

export const a2bFulfillmentWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.A2B_FULFILLMENT_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedA2bFulfillmentWebhookTopics)).required(),
  })
  .strict()

export const mayaWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.MAYA_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedMayaWebhookTopics)).required(),
  })
  .strict()

export const misfitsMarketWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.MISFITS_MARKET_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedMisfitsMarketWebhookTopics)).required(),
  })
  .strict()

export const mintsoftWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.MINTSOFT_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedMintsoftWebhookTopics)).required(),
  })
  .strict()

export const firstbaseWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.FIRSTBASE_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedFirstbaseWebhookTopics)).required(),
  })
  .strict()

export const fenoWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.FENO_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedFenoWebhookTopics)).required(),
  })
  .strict()

export const navisphereWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.NAVISPHERE_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedNavisphereWebhookTopics)).required(),
    integrationId: optionalWebhookIntegrationInstanceId,
  })
  .strict()

export const tikTokShopWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.TIKTOK_SHOP_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedTikTokShopWebhookTopics)).required(),
    integrationId: optionalWebhookIntegrationInstanceId,
  })
  .strict()

export const usaSupplyPortalWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.USA_SUPPLY_PORTAL_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedUSASupplyPortalWebhookTopics)).required(),
  })
  .strict()

export const dossBankingWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.DOSS_BANKING_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedDossBankingWebhookTopics)).required(),
    integrationId: optionalWebhookIntegrationInstanceId,
  })
  .strict()

export const dossBillPayWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.DOSS_BILL_PAY_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedDossBillPayWebhookTopics)).required(),
  })
  .strict()

export const airtableWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.AIRTABLE_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedAirtableWebhookTopics)).required(),
    baseId: yup.string().required(),
    tableId: yup.string().required(),
  })
  .strict()

export const salsifyWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.SALSIFY_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedSalsifyWebhookTopics)).required(),
  })
  .strict()

export const shipWireWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.SHIPWIRE_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedShipWireWebhookTopics)).required(),
  })
  .strict()

export const silqWebhookAutoTriggerMetadataSchema = yup

  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.SILQ_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedSilqWebhookTopics)).required(),
  })
  .strict()

export const stordWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.STORD_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedStordWebhookTopics)).required(),
    integrationId: optionalWebhookIntegrationInstanceId,
  })
  .strict()

export const fortySeasWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.FORTY_SEAS_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedFortySeasWebhookTopics)).required(),
  })
  .strict()

export const routableWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.ROUTABLE_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedRoutableWebhookTopics)).required(),
  })
  .strict()

export const easyPostWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.EASYPOST_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedEasyPostWebhookTopics)).required(),
  })
  .strict()
export const walmartWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.WALMART_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedWalmartWebhookTopics)).required(),
  })
  .strict()

export const rilletWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.RILLET_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedRilletWebhookTopics)).required(),
    integrationId: optionalWebhookIntegrationInstanceId,
  })
  .strict()

export const jazzWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.JAZZ_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedJazzWebhookTopics)).required(),
  })
  .strict()

export const shopeeWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.SHOPEE_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedShopeeWebhookTopics)).required(),
  })
  .strict()

export const lazadaWebhookAutoTriggerMetadataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionAutoTriggerType.LAZADA_WEBHOOK] as const)
      .required(),
    topic: yup.string().oneOf(Object.values(SupportedLazadaWebhookTopics)).required(),
  })
  .strict()

// Inferred types

export type FormSubmitAutoTriggerMetadata = yup.InferType<
  typeof formSubmitAutoTriggerMetadataSchema
>

export type FormV2SubmitAutoTriggerMetadata = yup.InferType<
  typeof formV2SubmitAutoTriggerMetadataSchema
>

export type AddRowAutoTriggerMetadata = yup.InferType<typeof addRowAutoTriggerMetadataSchema>

export type UpdateCellAutoTriggerMetadata = yup.InferType<
  typeof updateCellAutoTriggerMetadataSchema
>

export type UpdateRowAutoTriggerMetadata = yup.InferType<typeof updateRowAutoTriggerMetadataSchema>

export type DeleteRowAutoTriggerMetadata = yup.InferType<typeof deleteRowAutoTriggerMetadataSchema>

export type DemoAutoTriggerMetadata = yup.InferType<typeof demoAutoTriggerMetadataSchema>

export type ShopifyWebhookAutoTriggerMetadata = yup.InferType<
  typeof shopifyWebhookAutoTriggerMetadataSchema
>

export type QuickbooksWebhookAutoTriggerMetadata = yup.InferType<
  typeof quickbooksWebhookAutoTriggerMetadataSchema
>

export type GmailWebhookAutoTriggerMetadata = yup.InferType<
  typeof gmailWebhookAutoTriggerMetadataSchema
>

export type GnosisWebhookAutoTriggerMetadata = yup.InferType<
  typeof gnosisWebhookAutoTriggerMetadataSchema
>

export type OutlookWebhookAutoTriggerMetadata = yup.InferType<
  typeof outlookWebhookAutoTriggerMetadataSchema
>

export type WooCommerceWebhookAutoTriggerMetadata = yup.InferType<
  typeof wooCommerceWebhookAutoTriggerMetadataSchema
>

export type LoopReturnsWebhookAutoTriggerMetadata = yup.InferType<
  typeof loopReturnsWebhookAutoTriggerMetadataSchema
>

export type LogiwaWebhookAutoTriggerMetadata = yup.InferType<
  typeof logiwaWebhookAutoTriggerMetadataSchema
>

export type ThinkLogisticsWebhookAutoTriggerMetadata = yup.InferType<
  typeof thinkLogisticsWebhookAutoTriggerMetadataSchema
>

export type LocadWebhookAutoTriggerMetadata = yup.InferType<
  typeof locadWebhookAutoTriggerMetadataSchema
>

export type ShipHeroWebhookAutoTriggerMetadata = yup.InferType<
  typeof shipHeroWebhookAutoTriggerMetadataSchema
>

export type ShipMonkWebhookAutoTriggerMetadata = yup.InferType<
  typeof shipMonkWebhookAutoTriggerMetadataSchema
>

export type CartonCloudWebhookAutoTriggerMetadata = yup.InferType<
  typeof cartonCloudWebhookAutoTriggerMetadataSchema
>

export type FlexportWebhookAutoTriggerMetadata = yup.InferType<
  typeof flexportWebhookAutoTriggerMetadataSchema
>

export type ExtensivWebhookAutoTriggerMetadata = yup.InferType<
  typeof extensivWebhookAutoTriggerMetadataSchema
>

export type StripeWebhookAutoTriggerMetadata = yup.InferType<
  typeof stripeWebhookAutoTriggerMetadataSchema
>

export type BillWebhookAutoTriggerMetadata = yup.InferType<
  typeof billWebhookAutoTriggerMetadataSchema
>

export type IngramMicroWebhookAutoTriggerMetadata = yup.InferType<
  typeof ingramMicroWebhookAutoTriggerMetadataSchema
>

export type JayGroupWebhookAutoTriggerMetadata = yup.InferType<
  typeof jayGroupWebhookAutoTriggerMetadataSchema
>

export type GofreightWebhookAutoTriggerMetadata = yup.InferType<
  typeof gofreightWebhookAutoTriggerMetadataSchema
>

export type AmazonSellerWebhookAutoTriggerMetadata = yup.InferType<
  typeof amazonSellerWebhookAutoTriggerMetadataSchema
>

export type HubSpotWebhookAutoTriggerMetadata = yup.InferType<
  typeof hubSpotWebhookAutoTriggerMetadataSchema
>

export type ShipBobWebhookAutoTriggerMetadata = yup.InferType<
  typeof shipBobWebhookAutoTriggerMetadataSchema
>

export type A2bFulfillmentWebhookAutoTriggerMetadata = yup.InferType<
  typeof a2bFulfillmentWebhookAutoTriggerMetadataSchema
>

export type MayaWebhookAutoTriggerMetadata = yup.InferType<
  typeof mayaWebhookAutoTriggerMetadataSchema
>

export type MisfitsMarketWebhookAutoTriggerMetadata = yup.InferType<
  typeof misfitsMarketWebhookAutoTriggerMetadataSchema
>

export type MintsoftWebhookAutoTriggerMetadata = yup.InferType<
  typeof mintsoftWebhookAutoTriggerMetadataSchema
>

export type FirstbaseWebhookAutoTriggerMetadata = yup.InferType<
  typeof firstbaseWebhookAutoTriggerMetadataSchema
>

export type FenoWebhookAutoTriggerMetadata = yup.InferType<
  typeof fenoWebhookAutoTriggerMetadataSchema
>

export type NavisphereWebhookAutoTriggerMetadata = yup.InferType<
  typeof navisphereWebhookAutoTriggerMetadataSchema
>

export type TikTokShopWebhookAutoTriggerMetadata = yup.InferType<
  typeof tikTokShopWebhookAutoTriggerMetadataSchema
>

export type USASupplyPortalWebhookAutoTriggerMetadata = yup.InferType<
  typeof usaSupplyPortalWebhookAutoTriggerMetadataSchema
>

export type DossBankingWebhookAutoTriggerMetadata = yup.InferType<
  typeof dossBankingWebhookAutoTriggerMetadataSchema
>

export type DossBillPayWebhookAutoTriggerMetadata = yup.InferType<
  typeof dossBillPayWebhookAutoTriggerMetadataSchema
>

export type AirtableWebhookAutoTriggerMetadata = yup.InferType<
  typeof airtableWebhookAutoTriggerMetadataSchema
>

export type SalsifyWebhookAutoTriggerMetadata = yup.InferType<
  typeof salsifyWebhookAutoTriggerMetadataSchema
>

export type ShipWireWebhookAutoTriggerMetadata = yup.InferType<
  typeof shipWireWebhookAutoTriggerMetadataSchema
>

export type SilqWebhookAutoTriggerMetadata = yup.InferType<
  typeof silqWebhookAutoTriggerMetadataSchema
>

export type StordWebhookAutoTriggerMetadata = yup.InferType<
  typeof stordWebhookAutoTriggerMetadataSchema
>

export type FortySeasWebhookAutoTriggerMetadata = yup.InferType<
  typeof fortySeasWebhookAutoTriggerMetadataSchema
>

export type RoutableWebhookAutoTriggerMetadata = yup.InferType<
  typeof routableWebhookAutoTriggerMetadataSchema
>

export type EasyPostWebhookAutoTriggerMetadata = yup.InferType<
  typeof easyPostWebhookAutoTriggerMetadataSchema
>

export type WalmartWebhookAutoTriggerMetadata = yup.InferType<
  typeof walmartWebhookAutoTriggerMetadataSchema
>

export type RilletWebhookAutoTriggerMetadata = yup.InferType<
  typeof rilletWebhookAutoTriggerMetadataSchema
>

export type JazzWebhookAutoTriggerMetadata = yup.InferType<
  typeof jazzWebhookAutoTriggerMetadataSchema
>

export type ShopeeWebhookAutoTriggerMetadata = yup.InferType<
  typeof shopeeWebhookAutoTriggerMetadataSchema
>

export type LazadaWebhookAutoTriggerMetadata = yup.InferType<
  typeof lazadaWebhookAutoTriggerMetadataSchema
>

export type AutoTriggerMetadata =
  | FormSubmitAutoTriggerMetadata
  | FormV2SubmitAutoTriggerMetadata
  | AddRowAutoTriggerMetadata
  | UpdateCellAutoTriggerMetadata
  | UpdateRowAutoTriggerMetadata
  | DeleteRowAutoTriggerMetadata
  | ShopifyWebhookAutoTriggerMetadata
  | DemoAutoTriggerMetadata
  | QuickbooksWebhookAutoTriggerMetadata
  | OutlookWebhookAutoTriggerMetadata
  | LogiwaWebhookAutoTriggerMetadata
  | ThinkLogisticsWebhookAutoTriggerMetadata
  | LocadWebhookAutoTriggerMetadata
  | GmailWebhookAutoTriggerMetadata
  | GnosisWebhookAutoTriggerMetadata
  | WooCommerceWebhookAutoTriggerMetadata
  | LoopReturnsWebhookAutoTriggerMetadata
  | ShipHeroWebhookAutoTriggerMetadata
  | ExtensivWebhookAutoTriggerMetadata
  | FlexportWebhookAutoTriggerMetadata
  | StripeWebhookAutoTriggerMetadata
  | BillWebhookAutoTriggerMetadata
  | IngramMicroWebhookAutoTriggerMetadata
  | JayGroupWebhookAutoTriggerMetadata
  | GofreightWebhookAutoTriggerMetadata
  | AmazonSellerWebhookAutoTriggerMetadata
  | HubSpotWebhookAutoTriggerMetadata
  | SalsifyWebhookAutoTriggerMetadata
  | ShipBobWebhookAutoTriggerMetadata
  | ShipMonkWebhookAutoTriggerMetadata
  | CartonCloudWebhookAutoTriggerMetadata
  | A2bFulfillmentWebhookAutoTriggerMetadata
  | MayaWebhookAutoTriggerMetadata
  | MisfitsMarketWebhookAutoTriggerMetadata
  | MintsoftWebhookAutoTriggerMetadata
  | FirstbaseWebhookAutoTriggerMetadata
  | FenoWebhookAutoTriggerMetadata
  | NavisphereWebhookAutoTriggerMetadata
  | TikTokShopWebhookAutoTriggerMetadata
  | USASupplyPortalWebhookAutoTriggerMetadata
  | DossBankingWebhookAutoTriggerMetadata
  | DossBillPayWebhookAutoTriggerMetadata
  | AirtableWebhookAutoTriggerMetadata
  | RoutableWebhookAutoTriggerMetadata
  | SilqWebhookAutoTriggerMetadata
  | ShipWireWebhookAutoTriggerMetadata
  | StordWebhookAutoTriggerMetadata
  | FortySeasWebhookAutoTriggerMetadata
  | EasyPostWebhookAutoTriggerMetadata
  | WalmartWebhookAutoTriggerMetadata
  | RilletWebhookAutoTriggerMetadata
  | JazzWebhookAutoTriggerMetadata
  | ShopeeWebhookAutoTriggerMetadata
  | LazadaWebhookAutoTriggerMetadata
/**
 * Every auto-trigger metadata schema, keyed by its trigger type — the single registry other
 * code derives from (validation guards, the WaC webhook event catalog). The
 * `Record<ActionAutoTriggerType, …>` constraint makes registration of a new trigger type a
 * compile error to forget.
 */
export const autoTriggerMetadataSchemasByType = {
  [ActionAutoTriggerType.FORM_SUBMIT]: formSubmitAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.FORM_V2_SUBMIT]: formV2SubmitAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.ADD_ROW]: addRowAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.UPDATE_CELL]: updateCellAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.UPDATE_ROW]: updateRowAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.DELETE_ROW]: deleteRowAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.DEMO]: demoAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.A2B_FULFILLMENT_WEBHOOK]: a2bFulfillmentWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.AIRTABLE_WEBHOOK]: airtableWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.AMAZON_SELLER_WEBHOOK]: amazonSellerWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.BILL_WEBHOOK]: billWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.CARTONCLOUD_WEBHOOK]: cartonCloudWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.DOSS_BANKING_WEBHOOK]: dossBankingWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.DOSS_BILL_PAY_WEBHOOK]: dossBillPayWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.EASYPOST_WEBHOOK]: easyPostWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.EXTENSIV_WEBHOOK]: extensivWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.FENO_WEBHOOK]: fenoWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.FIRSTBASE_WEBHOOK]: firstbaseWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.FLEXPORT_WEBHOOK]: flexportWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.FORTY_SEAS_WEBHOOK]: fortySeasWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.GMAIL_WEBHOOK]: gmailWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.GNOSIS_WEBHOOK]: gnosisWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.GOFREIGHT_WEBHOOK]: gofreightWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.HUBSPOT_WEBHOOK]: hubSpotWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.INGRAM_MICRO_WEBHOOK]: ingramMicroWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.JAY_GROUP_WEBHOOK]: jayGroupWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.JAZZ_WEBHOOK]: jazzWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.LAZADA_WEBHOOK]: lazadaWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.LOCAD_WEBHOOK]: locadWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.LOGIWA_WEBHOOK]: logiwaWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.LOOP_RETURNS_WEBHOOK]: loopReturnsWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.MAYA_WEBHOOK]: mayaWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.MINTSOFT_WEBHOOK]: mintsoftWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.MISFITS_MARKET_WEBHOOK]: misfitsMarketWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.NAVISPHERE_WEBHOOK]: navisphereWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.OUTLOOK_WEBHOOK]: outlookWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.QUICKBOOKS_WEBHOOK]: quickbooksWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.RILLET_WEBHOOK]: rilletWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.ROUTABLE_WEBHOOK]: routableWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.SALSIFY_WEBHOOK]: salsifyWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.SHIPBOB_WEBHOOK]: shipBobWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.SHIPHERO_WEBHOOK]: shipHeroWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.SHIPMONK_WEBHOOK]: shipMonkWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.SHIPWIRE_WEBHOOK]: shipWireWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.SHOPEE_WEBHOOK]: shopeeWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.SHOPIFY_WEBHOOK]: shopifyWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.SILQ_WEBHOOK]: silqWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.STORD_WEBHOOK]: stordWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.STRIPE_WEBHOOK]: stripeWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.THINK_LOGISTICS_WEBHOOK]: thinkLogisticsWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.TIKTOK_SHOP_WEBHOOK]: tikTokShopWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.USA_SUPPLY_PORTAL_WEBHOOK]:
    usaSupplyPortalWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.WALMART_WEBHOOK]: walmartWebhookAutoTriggerMetadataSchema,
  [ActionAutoTriggerType.WOO_COMMERCE_WEBHOOK]: wooCommerceWebhookAutoTriggerMetadataSchema,
} as const satisfies Readonly<
  Record<ActionAutoTriggerType, { isValidSync(value: unknown): boolean }>
>

/**
 * Checks if the given metadata is a valid action trigger metadata object
 */
export function isAutoTriggerMetadata(metadata: unknown): metadata is AutoTriggerMetadata {
  if (!_.isPlainObject(metadata)) {
    return false
  }

  const type = _.get(metadata, 'type')
  if (!_.isString(type)) {
    return false
  }
  const triggerType = Object.values(ActionAutoTriggerType).find((value) => value === type)
  if (_.isNil(triggerType)) {
    return false
  }

  return autoTriggerMetadataSchemasByType[triggerType].isValidSync(metadata)
}
